try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsxs, jsx } from 'react/jsx-runtime';
import { isBrowser } from '@phx-husky/browser-helpers';
import classNames from 'classnames';
import { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@design-system/button';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const ModalHeader = ({ topElements, showCloseButton = true, closeAriaLabel, closeModal, closeIconSize, }) => (jsxs("header", Object.assign({ className: "cds-modal-header" }, { children: [showCloseButton && (jsx("div", Object.assign({ className: "cds-modal__close-button-container" }, { children: jsx(Button, Object.assign({ className: "cds-modal__close-button", "aria-label": closeAriaLabel || 'Close modal', onClick: closeModal, variant: "subtle-icon", iconPrefix: { props: { icon: 'close' } }, size: closeIconSize, autoFocus: true }, { children: closeAriaLabel || 'Close' })) }))), topElements && (jsx("div", Object.assign({ className: "cds-modal__top-elements" }, { children: topElements })))] })));

const findFocusableElements = (modalRef) => modalRef.current.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]');
const handleTabKey = (e, modalRef) => {
    if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = findFocusableElements(modalRef);
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
        else if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        }
    }
};

const ModalWrapper = ({ children, className, fullPage = false, showOverlay = true, disableOnClickClose = false, close, ariaLabel, }) => {
    const modalRef = useRef(null);
    const handleOverlayClick = (_) => {
        if (!disableOnClickClose && close) {
            close();
        }
    };
    const handleContainerClick = (e) => {
        e.stopPropagation();
    };
    const handleKeyDown = (e) => {
        handleTabKey(e, modalRef);
    };
    return (jsxs("div", Object.assign({ className: classNames('cds-modal', className, {
            'cds-modal--full-page': fullPage,
        }), role: "presentation" }, { children: [showOverlay && (jsx("div", { className: "cds-modal__overlay", onClick: handleOverlayClick, role: "presentation" })), jsx("div", Object.assign({ className: "cds-modal__wrapper", "aria-modal": "true", role: "dialog", "data-testid": "modal-wrapper", ref: modalRef, "aria-label": ariaLabel || 'Modal' }, { children: jsx("div", Object.assign({ className: classNames('cds-modal__container', {
                        'cds-modal__container--full-page': fullPage,
                    }), onClick: handleContainerClick, onKeyDown: handleKeyDown, role: "presentation" }, { children: children })) }))] })));
};

const Modal = (_a) => {
    var { isShowing, topElements, children, close, closeModal, disableOnClickClose, className, ariaLabel, containerElement, fullPage, showOverlay = true, closeAriaLabel = 'close', closeIconSize = 'small', showCloseButton = true, mode = 'default', showHeader = true } = _a, rest = __rest(_a, ["isShowing", "topElements", "children", "close", "closeModal", "disableOnClickClose", "className", "ariaLabel", "containerElement", "fullPage", "showOverlay", "closeAriaLabel", "closeIconSize", "showCloseButton", "mode", "showHeader"]);
    const portalContainer = containerElement || (isBrowser() ? document.body : null);
    useEffect(() => {
        if (!isShowing || !close)
            return undefined;
        const handleEscape = (event) => {
            if (event.key === 'Escape')
                close();
        };
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'auto';
        };
    }, [isShowing, close]);
    if (!isShowing || !portalContainer)
        return null;
    return ReactDOM.createPortal(jsxs(ModalWrapper, Object.assign({ className: classNames(className, {
            'cds-modal--inverse-mode': mode !== 'default',
        }), fullPage: fullPage, showOverlay: showOverlay, disableOnClickClose: disableOnClickClose, close: close, ariaLabel: ariaLabel }, rest, { children: [showHeader && (jsx(ModalHeader, { topElements: topElements, closeAriaLabel: closeAriaLabel, closeModal: closeModal, closeIconSize: closeIconSize, showCloseButton: showCloseButton })), jsx("div", Object.assign({ className: "cds-modal__content" }, { children: children }))] })), portalContainer);
};

export { Modal };
